// Turning the board's two date pickers into a query for the events endpoint.
//
// Its own module, importing nothing, for two reasons: the hook around it needs
// React and cannot be loaded by the test suite, and this is the part that gets
// timezones wrong. `order_events.createdAt` is UTC; the admin picking «13/8»
// means their own day, and in Greece that day starts at 21:00 UTC on the 12th.
// Building the bounds from the date string alone puts a whole evening rush in
// the wrong day — which on a delivery board is most of the day's rows.

// The backend keeps this many days, so it is the floor under the picker. Asking
// for anything older comes back empty and reads as the feature being broken.
export const RETENTION_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

// 'YYYY-MM-DD' as the instant that day starts / ends in the browser's own zone.
// `new Date('2026-08-13T00:00:00')` — no Z, no offset — is parsed as local time,
// which is exactly what is wanted here.
export const startOfLocalDay = day => new Date(`${day}T00:00:00`);
export const endOfLocalDay = day => new Date(`${day}T23:59:59.999`);

// A Date as the 'YYYY-MM-DD' it falls on LOCALLY. toISOString() alone would give
// the UTC day, which after 21:00 in Greece is already tomorrow — so an admin
// opening the board at 23:00 would be offered a "today" they cannot pick.
export const toLocalDay = date =>
	new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

// What the pickers may offer: today back to the edge of retention.
export const selectableDays = ({ now = new Date(), retentionDays = RETENTION_DAYS } = {}) => ({
	newest: toLocalDay(now),
	oldest: toLocalDay(new Date(now.getTime() - retentionDays * DAY_MS)),
});

// Both ends are optional and mean different things when missing:
//
//   from only — "since that day", up to now
//   to only   — everything the table still holds, up to the end of that day
//   neither   — not filtering by date at all, which is null rather than a query
//
// `until` is omitted rather than set to now when there is no upper bound: the
// endpoint treats a missing one as open, and sending a timestamp would race the
// clock for no gain.
export const rangeToQuery = ({
	from,
	to,
	now = new Date(),
	retentionDays = RETENTION_DAYS,
} = {}) => {
	if (!from && !to) {
		return null;
	}

	const since = from ? startOfLocalDay(from) : new Date(now.getTime() - retentionDays * DAY_MS);
	const until = to ? endOfLocalDay(to) : null;

	// A backwards range is the picker being mid-edit, not a question. Answering
	// it with an empty board would look like "nothing happened".
	if (until && until.getTime() < since.getTime()) {
		return null;
	}

	return {
		since: since.toISOString(),
		...(until ? { until: until.toISOString() } : {}),
	};
};
